import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Rating } from "types/feedback";
import {
  AdditionalFeedbackQuestion,
  AdditionalFeedbackType,
  AdditionalFeedbackValue,
} from "types/additionalFeedback";
import Checkbox from "Components/Atoms/Checkbox";
import Typography from "Components/Atoms/Typography";
import { toEuropeanDate } from "utils/helper";
import RatingStars from "./RatingStars";
import Box from "Components/Atoms/Box";
import { LanguageContext } from "Contexts/LanguageContext";
import { translationsHelper } from "CustomHooks/useTranslations";

export type ReviewRowProps = {
  review: Rating;
  onDone: (id: string) => void;
  additionalFeedbackQuestions?: AdditionalFeedbackQuestion[];
};

const isMeaningfulAdditionalFeedbackValue = (
  value: AdditionalFeedbackValue | undefined
) => value !== undefined && value !== null && value !== "" && value !== false;

const fallbackLabel = (key: string) =>
  key
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ReviewRow = ({
  review,
  onDone,
  additionalFeedbackQuestions = [],
}: ReviewRowProps) => {
  const { t } = useTranslation(["feedback", "common"]);
  const {
    guest,
    date = "-",
    time = "-",
    tables = [],
    guests,
    comment = "",
    contact,
    rating,
    done = false,
    id,
    additionalFeedback = {},
  } = review;

  const [open, setOpen] = useState(false);
  const { language } = useContext(LanguageContext);

  const additionalFeedbackEntries = useMemo(() => {
    const configuredEntries = additionalFeedbackQuestions
      .map((question) => {
        const value = additionalFeedback?.[question.key];

        if (!isMeaningfulAdditionalFeedbackValue(value)) {
          return null;
        }

        return {
          key: question.key,
          label:
            translationsHelper<string>(language, question.label) ||
            fallbackLabel(question.key),
          type: question.type,
          value,
        };
      })
      .filter(Boolean) as {
      key: string;
      label: string;
      type: AdditionalFeedbackType;
      value: AdditionalFeedbackValue;
    }[];

    const configuredKeys = new Set(configuredEntries.map((entry) => entry.key));

    const orphanEntries = Object.entries(additionalFeedback ?? {})
      .filter(
        ([key, value]) =>
          !configuredKeys.has(key) && isMeaningfulAdditionalFeedbackValue(value)
      )
      .map(([key, value]) => ({
        key,
        label: fallbackLabel(key),
        type:
          typeof value === "number"
            ? "rating"
            : typeof value === "boolean"
            ? "checkbox"
            : "text",
        value,
      }));

    return [...configuredEntries, ...orphanEntries];
  }, [additionalFeedback, additionalFeedbackQuestions, language]);

  const hasAdditionalFeedback =
    rating <= 3 && additionalFeedbackEntries.length > 0;
  const additionalFeedbackPreviewEntries = additionalFeedbackEntries.slice(0, 2);
  const additionalFeedbackOverflowCount = Math.max(
    additionalFeedbackEntries.length - additionalFeedbackPreviewEntries.length,
    0
  );

  const getCompactAdditionalFeedbackValue = (
    value: AdditionalFeedbackValue,
    type: AdditionalFeedbackType
  ) => {
    if (type === "rating" && typeof value === "number") {
      return `${value}/5`;
    }

    if (type === "checkbox" && typeof value === "boolean") {
      return t(value ? "Yes" : "No", { ns: "common" });
    }

    return null;
  };

  const renderAdditionalFeedbackValue = (
    value: AdditionalFeedbackValue,
    type: AdditionalFeedbackType
  ) => {
    if (type === "rating" && typeof value === "number") {
      return <RatingStars rating={value} />;
    }

    if (type === "checkbox") {
      return (
        <Typography variant="text-3" color="subdued" translation="common">
          Yes
        </Typography>
      );
    }

    return (
      <Typography variant="text-3" color="subdued">
        {value}
      </Typography>
    );
  };

  return (
    <>
      <tr
        onClick={() => setOpen((o) => !o)}
        className={`feedbackReviewRow ${open ? "is-open" : ""} ${
          hasAdditionalFeedback ? "has-additional-feedback" : ""
        }`}
      >
        <td style={{ textAlign: "center", padding: 9 }}>
          <Checkbox
            id={id}
            checked={done || rating > 3}
            onChange={() => onDone(id)}
            onClick={(e) => e.stopPropagation()}
            label={""}
            translation="common"
            color="primary"
            size="small"
            style={{ margin: "auto", width: "fit-content" }}
          />
        </td>
        <td>
          <Typography variant="text-3" color="subdued">
            {guest.name}
          </Typography>
        </td>
        <td>
          <Typography variant="text-3" color="subdued">
            {toEuropeanDate(date)}
          </Typography>
        </td>
        <td>
          <Typography variant="text-3" color="subdued" translation={null}>
            {time}
          </Typography>
        </td>
        <td>
          <Typography variant="text-3" color="subdued">
            {tables.join(", ")}
          </Typography>
        </td>
        <td>
          <Typography variant="text-3" color="subdued">
            {guests}
          </Typography>
        </td>
        <td style={{ minWidth: 170 }}>
          <Box className="ratingCell">
            <RatingStars rating={rating} />
            {hasAdditionalFeedback && (
              <span className="ratingCell__badge">
                <Typography
                  variant="text-5"
                  color="primary"
                  translation={null}
                >
                  {t("followUpAnswers", {
                    count: additionalFeedbackEntries.length,
                  })}
                </Typography>
              </span>
            )}
          </Box>
        </td>
        <td>
          <Typography variant="text-3" color="subdued" translation="common">
            {contact ? "Yes" : "No"}
          </Typography>
        </td>
        <td
          rowSpan={open ? 2 : 1}
          className={open ? "bigColumn" : "bigColumn"}
        >
          <Box className="reviewCommentCell">
            <Typography
              variant="text-3"
              color="subdued"
              className={!open ? "reviewCommentText--clamped" : ""}
            >
              {comment || "-"}
            </Typography>
            {hasAdditionalFeedback && !open && (
              <Box className="additionalFeedbackPreview">
                {additionalFeedbackPreviewEntries.map(
                  ({ key, label, type, value }) => {
                    const compactValue = getCompactAdditionalFeedbackValue(
                      value,
                      type
                    );

                    return (
                      <span
                        key={key}
                        className="additionalFeedbackPreview__pill"
                      >
                        <Typography variant="text-5" translation={null}>
                          {compactValue ? `${label} ${compactValue}` : label}
                        </Typography>
                      </span>
                    );
                  }
                )}
                {additionalFeedbackOverflowCount > 0 && (
                  <span className="additionalFeedbackPreview__pill">
                    <Typography variant="text-5" translation={null}>
                      {t("moreFollowUps", {
                        count: additionalFeedbackOverflowCount,
                      })}
                    </Typography>
                  </span>
                )}
              </Box>
            )}
          </Box>
        </td>
      </tr>
      {open && (
        <tr className="feedbackReviewMetaRow">
          <td></td>
          <td colSpan={3}>
            <Box className="reviewMetaCell">
              <Typography
                variant="text-3"
                style={{ marginRight: 16 }}
                translation="feedback"
              >
                Email
              </Typography>
              <Typography variant="text-3" color="subdued">
                {guest.email}
              </Typography>
            </Box>
          </td>
          <td colSpan={3}>
            <Box className="reviewMetaCell">
              <Typography
                variant="text-3"
                style={{ marginRight: 16 }}
                translation="feedback"
              >
                Phone
              </Typography>
              <Typography variant="text-3" color="subdued">
                {guest.phone}
              </Typography>
            </Box>
          </td>
          <td></td>
        </tr>
      )}
      {open && hasAdditionalFeedback && (
        <tr className="feedbackReviewAdditionalRow">
          <td></td>
          <td colSpan={8}>
            <Box className="additionalFeedbackHeader">
              <Typography variant="text-3" translation="feedback">
                Additional feedback
              </Typography>
              <span className="additionalFeedbackCount">
                <Typography variant="text-5" color="primary" translation={null}>
                  {t("followUpAnswers", {
                    count: additionalFeedbackEntries.length,
                  })}
                </Typography>
              </span>
            </Box>
            <Box className="additionalFeedbackList">
              {additionalFeedbackEntries.map(({ key, label, type, value }) => (
                <Box key={key} className="additionalFeedbackItem">
                  <Typography
                    variant="text-4"
                    color="subdued"
                    className="additionalFeedbackItem__label"
                  >
                    {label}
                  </Typography>
                  <Box className="additionalFeedbackItem__value">
                    {renderAdditionalFeedbackValue(value, type)}
                  </Box>
                </Box>
              ))}
            </Box>
          </td>
        </tr>
      )}
    </>
  );
};

export default ReviewRow;
